import { Form } from "@/components/form/Form.js";
import { useFormState } from "@rosette/web_components/src/form/FormStateContext.js";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounce } from "@rosette/web_utils/src/useDebounce.js";
import { motion } from "framer-motion";
import { controlledFadeInOut } from "@/utils/fadeInOut.js";


interface AutoSavingStatusProps {
    registerChangeListener: (listener: () => void) => () => void;
}


function AutoSavingStatus({ registerChangeListener }: AutoSavingStatusProps) {
    const {
        submitFormError,
        submitFormIsPending,
        submitFormSuccessMessage,
        submitForm,
        updateCachedFormData,
    } = useFormState();

    const [changed, setChanged] = useState(false);
    const changedRef = useRef<boolean>(false);

    // Submit the form to save
    const save = useCallback(() => {
        if (changedRef.current) {
            changedRef.current = false;
            setChanged(false);
            submitForm();
        }
    }, [changedRef, setChanged, submitForm]);
    const debouncedAutoSave = useDebounce(save);

    // Listen for changes to the form
    useEffect(() => registerChangeListener(() => {
        updateCachedFormData();
        changedRef.current = true;
        setChanged(true);
        debouncedAutoSave();
    }), [registerChangeListener, changedRef, setChanged, debouncedAutoSave, updateCachedFormData]);

    // Save on unmount
    useEffect(() => save, [save]);

    // Report status to user
    let statusText = null;
    if (submitFormError) {
        statusText = `An error occurred: ${submitFormError}`;
    } else if (changed || submitFormIsPending) {
        statusText = "Saving...";
    } else if (submitFormSuccessMessage) {
        statusText = "Saved";
    }
    return (
        <motion.div
            className="mt-4 mb-2 block min-h-[1em] text-base leading-[1em] text-subtle"
            {...controlledFadeInOut(!!statusText)}>

            {statusText}
        </motion.div>
    );
}


interface AutoSavingFormProps {
    className?: string;
    action: string;
    registerChangeListener: (listener: () => void) => () => void;
    acceptUpdateJSON: (json: any) => void;
    validationError: string | null;
    children?: ReactNode;
}


export function AutoSavingForm({
    className, action, registerChangeListener,
    acceptUpdateJSON, validationError, children,
}: AutoSavingFormProps) {
    return (
        <Form
            className={className}
            action={action}
            validationError={validationError}
            skipRefreshOnSubmit={true}
            acceptResponseJSON={acceptUpdateJSON}>

            {children}

            <AutoSavingStatus registerChangeListener={registerChangeListener} />
        </Form>
    );
}
